import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bookmark, Share2, Check, Loader2 } from "lucide-react";

// Save a generated item and produce a shareable public link.
export const ResultActions = ({ kind, data, onRequireAuth }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);

  const handle402 = (e) => {
    if (e?.response?.status === 402) {
      toast.error(e.response.data.detail, { action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      return true;
    }
    return false;
  };

  const ensureSaved = async () => {
    if (savedId) return savedId;
    const { data: item } = await api.post("/saved", { kind, data });
    setSavedId(item.id);
    return item.id;
  };

  const handleSave = async () => {
    if (!user) return onRequireAuth?.();
    setSaving(true);
    try {
      await ensureSaved();
      toast.success("Saved to your collection.");
    } catch (e) {
      if (!handle402(e)) toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    if (!user) return onRequireAuth?.();
    setSaving(true);
    try {
      const id = await ensureSaved();
      const url = `${window.location.origin}/share/${id}`;
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard!");
    } catch (e) {
      if (!handle402(e)) toast.error("Could not create share link.");
    } finally {
      setSaving(false);
    }
  };

  const saveIcon = saving
    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
    : savedId
      ? <Check className="mr-2 h-4 w-4 text-primary" />
      : <Bookmark className="mr-2 h-4 w-4" />;

  return (
    <div className="flex flex-wrap gap-3">
      <Button data-testid="save-result-btn" onClick={handleSave} disabled={saving} variant="outline" className="rounded-sm border-white/10 bg-transparent uppercase tracking-widest hover:border-primary/50 hover:bg-primary/10">
        {saveIcon}
        {savedId ? "Saved" : "Save"}
      </Button>
      <Button data-testid="share-result-btn" onClick={handleShare} disabled={saving} variant="outline" className="rounded-sm border-white/10 bg-transparent uppercase tracking-widest hover:border-primary/50 hover:bg-primary/10">
        <Share2 className="mr-2 h-4 w-4" /> Share
      </Button>
    </div>
  );
};

export default ResultActions;
